import { RailAccordion } from './RailAccordion'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth'
import { useOrg } from '../org'
import { Icon, Mark } from '../brand/Icon'
import { useBrandName } from '../brand/pack'
import { useT } from '../i18n'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  onNavigate?: () => void
  /** callback-ref DOM-слота инструментов раздела (портал MapsPage) */
  toolsRef?: (el: HTMLDivElement | null) => void
}

export function Sidebar({ collapsed, onToggle, onNavigate, toolsRef }: SidebarProps) {
  const t = useT()
  // Имя бренда — из пака домена; ключ каталога остаётся запасным (см. brand/pack).
  const brandName = useBrandName()
  return (
    <div className="flex h-full flex-col border-r border-hull bg-deep text-foam">
      {/* ── Бренд ── */}
      {collapsed ? (
        <button
          type="button"
          onClick={onToggle}
          title={t('nav.expandMenu')}
          className="flex h-14 w-full items-center justify-center border-b border-hull-hair text-foam hover:bg-haze"
        >
          <Mark className="h-7 w-7" />
        </button>
      ) : (
        <div className="flex h-14 items-center gap-2.5 border-b border-hull-hair px-3.5">
          <Mark className="h-7 w-7 shrink-0" />
          <span className="font-heading text-[17px] font-extrabold leading-none tracking-tight text-foam">
            {brandName}
          </span>
          <button
            type="button"
            onClick={onToggle}
            title={t('nav.collapseMenu')}
            className="ml-auto rounded-lg p-1.5 text-mist hover:bg-haze hover:text-foam"
          >
            <Icon name="ic-panel" className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* ── Навигация-гармошка: разделы + инструменты активного раздела внутри ── */}
      <div className="console-scroll flex-1 overflow-y-auto">
        <RailAccordion collapsed={collapsed} onNavigate={onNavigate} toolsRef={toolsRef} />
      </div>

      {/* ── Подвал: кто я и чья организация; всё прочее — в «Настройках» ── */}
      <div className="border-t border-hull-hair p-2">
        <IdentityCard collapsed={collapsed} onNavigate={onNavigate} />
      </div>
    </div>
  )
}

/** Компактная карточка «пользователь · организация»; клик ведёт в профиль. */
function IdentityCard({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const t = useT()
  const { user } = useAuth()
  const { activeOrg, isLoading } = useOrg()
  const email = user?.email || '—'
  const org = activeOrg?.name ?? (isLoading ? '…' : t('org.none'))
  const title = `${t('nav.identity')}: ${email} · ${org}`
  return (
    <Link
      to="/settings/profile"
      onClick={onNavigate}
      title={title}
      aria-label={title}
      className={[
        'flex items-center rounded-lg text-mist transition-colors hover:bg-haze hover:text-foam',
        collapsed ? 'w-full justify-center py-2' : 'w-full gap-2.5 px-2.5 py-1.5',
      ].join(' ')}
    >
      <Icon name="ic-profile" className="h-5 w-5 shrink-0" />
      {!collapsed && (
        <span className="min-w-0 leading-tight">
          <span className="block truncate text-sm text-foam">{email}</span>
          <span className="block truncate text-xs">{org}</span>
        </span>
      )}
    </Link>
  )
}
